import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  ArrowLeft, Plus, Pause, Play, Trash2, Copy, KeyRound, User as UserIcon, Building2,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  useAdminAccount, useAdminApiKeys, useAdminUsers,
  useActivateAccount, useCreateApiKey, useCreateUser, useDeleteApiKey, useDeleteUser,
  usePatchAccount, useRevokeApiKey, useSuspendAccount,
} from "@/hooks/use-admin";
import type { AdminApiKeyCreatedOut } from "@/lib/api";

export const Route = createFileRoute("/_auth/admin/accounts/$accountId")({
  component: AccountDetail,
});

type Tab = "info" | "users" | "apikeys";

function AccountDetail() {
  const { accountId } = Route.useParams();
  const id = Number(accountId);
  const [tab, setTab] = useState<Tab>("info");

  const account = useAdminAccount(id);
  const suspend = useSuspendAccount();
  const activate = useActivateAccount();

  return (
    <div className="px-6 py-6 max-w-[1480px] mx-auto">
      <header className="mb-6">
        <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-ink-500 hover:text-ink-700 mb-2">
          <ArrowLeft size={14} /> Volver a cuentas
        </Link>
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Building2 size={20} className="text-ink-400" />
              <h1 className="text-2xl font-bold text-ink-900 dark:text-white">
                {account.data?.name ?? "Cargando…"}
              </h1>
              {account.data && (
                <>
                  <Badge variant={account.data.status === "active" ? "good" : "bad"}>{account.data.status}</Badge>
                  <Badge variant="muted">{account.data.type}</Badge>
                </>
              )}
            </div>
            {account.data && (
              <p className="text-sm text-ink-500 mt-1">
                {account.data.users_count} usuarios · {account.data.projects_count} proyectos
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {account.data?.status === "active" ? (
              <Button variant="outline" onClick={() => suspend.mutate(id)} disabled={suspend.isPending}>
                <Pause size={14} /> Suspender
              </Button>
            ) : (
              <Button variant="outline" onClick={() => activate.mutate(id)} disabled={activate.isPending}>
                <Play size={14} /> Activar
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-ink-200 dark:border-ink-800 mb-4">
        {[
          ["info", "Información"],
          ["users", "Usuarios"],
          ["apikeys", "API Keys"],
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key as Tab)}
            className={cn(
              "h-10 px-4 text-sm font-medium border-b-2 transition-colors",
              tab === key
                ? "border-brand-600 text-brand-700 dark:text-brand-300"
                : "border-transparent text-ink-500 hover:text-ink-700",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "info" && <InfoTab id={id} account={account.data} />}
      {tab === "users" && <UsersTab id={id} />}
      {tab === "apikeys" && <ApiKeysTab id={id} />}
    </div>
  );
}

// ============ Tab Info ============

const infoSchema = z.object({
  name: z.string().min(2),
  tz: z.string().min(2),
});
type InfoInput = z.infer<typeof infoSchema>;

function InfoTab({ id, account }: { id: number; account: ReturnType<typeof useAdminAccount>["data"] }) {
  const patch = usePatchAccount(id);
  const form = useForm<InfoInput>({
    resolver: zodResolver(infoSchema),
    values: account ? { name: account.name, tz: account.tz } : undefined,
  });

  if (!account) return <Skeleton className="h-40" />;

  async function onSubmit(data: InfoInput) {
    try {
      await patch.mutateAsync(data);
    } catch {}
  }

  return (
    <div className="space-y-4">
      <Card>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3 max-w-md">
          <div>
            <Label>Nombre</Label>
            <Input className="mt-1" {...form.register("name")} />
          </div>
          <div>
            <Label>Zona horaria</Label>
            <Input className="mt-1" {...form.register("tz")} />
          </div>
          <p className="text-xs text-ink-500">
            Plan: {account.plan_code ?? "—"} · Creada {new Date(account.created_at).toLocaleString("es-PE")}
          </p>
          <Button type="submit" disabled={patch.isPending}>
            {patch.isPending ? "Guardando…" : "Guardar cambios"}
          </Button>
        </form>
      </Card>

      {account.type === "agencia" && <AgencyInfoPanel accountId={id} account={account} />}
      {account.type === "agencia" && <MediaBootstrapPanel accountId={id} />}
    </div>
  );
}

// ============ Panel info agencia (contacto + billing) ============

function AgencyInfoPanel({ accountId, account }: { accountId: number; account: NonNullable<ReturnType<typeof useAdminAccount>["data"]> }) {
  const patch = usePatchAccount(accountId);
  const [form, setForm] = useState({
    description: account.description ?? "",
    contact_email: account.contact_email ?? "",
    contact_phone: account.contact_phone ?? "",
    address: account.address ?? "",
    tax_id: account.tax_id ?? "",
    billing_email: account.billing_email ?? "",
    website: account.website ?? "",
    default_currency: account.default_currency ?? "PEN",
  });

  async function save() {
    try {
      await patch.mutateAsync({
        description: form.description || undefined,
        contact_email: form.contact_email || undefined,
        contact_phone: form.contact_phone || undefined,
        address: form.address || undefined,
        tax_id: form.tax_id || undefined,
        billing_email: form.billing_email || undefined,
        website: form.website || undefined,
        default_currency: form.default_currency,
      } as any);
      toast.success("Información agencia actualizada");
    } catch {}
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          🏢 Información de la agencia
        </CardTitle>
        <CardDescription>
          Datos de contacto y facturación. Usados en headers de reportes y como defaults para placements.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label className="text-xs">Descripción / lema</Label>
          <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Agencia de medios líder en Arequipa" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Email de contacto</Label>
            <Input type="email" value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              placeholder="contacto@holacliente.pe" />
          </div>
          <div>
            <Label className="text-xs">Teléfono</Label>
            <Input value={form.contact_phone}
              onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
              placeholder="+51 999 999 999" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Dirección</Label>
            <Input value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="Av. Ejército 123, Arequipa" />
          </div>
          <div>
            <Label className="text-xs">RUC / Tax ID</Label>
            <Input value={form.tax_id}
              onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
              maxLength={20} placeholder="20100000001" />
          </div>
          <div>
            <Label className="text-xs">Email facturación</Label>
            <Input type="email" value={form.billing_email}
              onChange={(e) => setForm({ ...form, billing_email: e.target.value })}
              placeholder="facturacion@holacliente.pe" />
          </div>
          <div>
            <Label className="text-xs">Website</Label>
            <Input value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              placeholder="https://holacliente.pe" />
          </div>
          <div>
            <Label className="text-xs">Moneda por defecto</Label>
            <Input value={form.default_currency}
              onChange={(e) => setForm({ ...form, default_currency: e.target.value.toUpperCase().slice(0, 3) })}
              maxLength={3} placeholder="PEN" className="font-mono" />
            <p className="text-[10px] text-ink-400 mt-1">Default para campañas y outlet rate cards.</p>
          </div>
        </div>
        <div className="flex justify-end pt-2">
          <Button onClick={save} disabled={patch.isPending}>
            {patch.isPending ? "Guardando…" : "Guardar información agencia"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


// ============ Panel bootstrap módulo Medios Tradicionales ============

function MediaBootstrapPanel({ accountId }: { accountId: number }) {
  const [seeding, setSeeding] = useState(false);
  const [loadingGeo, setLoadingGeo] = useState(false);
  const [seedingDemo, setSeedingDemo] = useState(false);
  const [geoStatus, setGeoStatus] = useState<{ regions: number; provinces: number; districts: number; mode: string } | null>(null);
  const [outletsResult, setOutletsResult] = useState<{ created: number } | null>(null);
  const [demoResult, setDemoResult] = useState<{ campaigns_created: number; ads_created: number; placements_created: number; report_url: string; client_portal_url: string } | null>(null);

  async function loadGeoStatus() {
    try {
      const { api } = await import("@/lib/api");
      const s = await api.admin.peruGeoStatus();
      setGeoStatus(s);
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  async function seedOutlets() {
    setSeeding(true);
    try {
      const { api } = await import("@/lib/api");
      const r = await api.admin.seedMediaOutlets(accountId);
      setOutletsResult({ created: r.created });
      toast.success(`${r.created} outlets creados en cuenta #${accountId}`);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSeeding(false);
    }
  }

  async function loadGeoFull() {
    setLoadingGeo(true);
    try {
      const { api } = await import("@/lib/api");
      const r = await api.admin.loadPeruGeoFull();
      setGeoStatus({ regions: r.regions, provinces: r.provinces, districts: r.districts, mode: r.mode });
      toast.success(`Geo INEI: ${r.districts} distritos cargados (modo ${r.mode})`);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoadingGeo(false);
    }
  }

  async function seedDemo() {
    setSeedingDemo(true);
    try {
      const { api } = await import("@/lib/api");
      const r = await api.admin.seedMediaDemo(accountId);
      setDemoResult({
        campaigns_created: r.campaigns_created,
        ads_created: r.ads_created,
        placements_created: r.placements_created,
        report_url: r.report_url,
        client_portal_url: r.client_portal_url,
      });
      toast.success(`Demo creado: ${r.campaigns_created} campañas, ${r.ads_created} avisos, ${r.placements_created} placements`);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSeedingDemo(false);
    }
  }

  // Auto-load status al montar
  if (geoStatus === null && !loadingGeo) {
    void loadGeoStatus();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          📻 Módulo Medios Tradicionales
        </CardTitle>
        <CardDescription>
          Bootstrap de catálogos para esta cuenta agencia. Operaciones idempotentes.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Outlets */}
        <div className="flex items-start justify-between gap-3 pb-3 border-b border-ink-100 dark:border-ink-800">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium">Catálogo de medios referenciales</div>
            <div className="text-xs text-ink-500 mt-0.5">
              ~27 outlets del PDF SEAL (El Peruano, RPP, Exitosa, HBA, etc.).
              {outletsResult && (
                <span className="text-emerald-600 ml-2">
                  ✓ {outletsResult.created} creados
                </span>
              )}
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={seedOutlets} disabled={seeding}>
            {seeding ? "Cargando…" : "Cargar outlets"}
          </Button>
        </div>

        {/* Geo */}
        <div className="flex items-start justify-between gap-3 pb-3 border-b border-ink-100 dark:border-ink-800">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium">Catálogo geográfico INEI Perú</div>
            <div className="text-xs text-ink-500 mt-0.5">
              {geoStatus
                ? `Estado: ${geoStatus.regions} regiones · ${geoStatus.provinces} provincias · ${geoStatus.districts} distritos (modo ${geoStatus.mode})`
                : "Cargando estado…"}
            </div>
            <div className="text-xs text-ink-400 mt-1">
              Global compartido entre todas las cuentas. Dataset completo: 25 + 196 + 1872.
            </div>
          </div>
          <Button
            size="sm"
            variant={geoStatus?.mode === "full" ? "outline" : "default"}
            onClick={loadGeoFull}
            disabled={loadingGeo}
          >
            {loadingGeo ? "Cargando…" : geoStatus?.mode === "full" ? "Recargar" : "Cargar completo"}
          </Button>
        </div>

        {/* Demo seeder */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium">🎬 Datos de demostración</div>
            <div className="text-xs text-ink-500 mt-0.5">
              Carga datos demo: 1 cliente "SEAL Demo", 4 campañas, ~40 avisos,
              ~280 placements en distintas regiones (6 meses), indicadores y 1 reporte publicado.
              {demoResult && (
                <span className="text-emerald-600 ml-2">
                  ✓ {demoResult.campaigns_created} campañas / {demoResult.ads_created} avisos / {demoResult.placements_created} placements
                </span>
              )}
            </div>
            {demoResult?.client_portal_url && (
              <a href={demoResult.client_portal_url} target="_blank" rel="noopener noreferrer"
                className="text-xs text-emerald-600 font-medium hover:underline mt-1 block">
                → Abrir portal de transparencia del cliente (enlace único)
              </a>
            )}
            {demoResult?.report_url && (
              <a href={demoResult.report_url} target="_blank" rel="noopener noreferrer"
                className="text-xs text-brand-600 hover:underline mt-1 inline-block">
                → Abrir enlace del reporte individual
              </a>
            )}
            <div className="text-[10px] text-ink-400 mt-1">
              Idempotente: si vuelves a ejecutar, no duplica entidades existentes.
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={seedDemo} disabled={seedingDemo}>
            {seedingDemo ? "Generando…" : "Crear demo"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============ Tab Users ============

const userSchema = z.object({
  name: z.string().min(2),
  email: z.string().email(),
  password: z.string().min(8),
  role: z.enum(["owner", "admin", "editor", "analyst", "responder", "viewer", "superadmin"]),
});
type UserInput = z.infer<typeof userSchema>;

function UsersTab({ id }: { id: number }) {
  const users = useAdminUsers(id);
  const deleteUser = useDeleteUser(id);
  const [showModal, setShowModal] = useState(false);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Usuarios</CardTitle>
          <CardDescription>{users.data?.length ?? 0} en esta cuenta</CardDescription>
        </div>
        <Button onClick={() => setShowModal(true)} size="sm">
          <Plus size={14} /> Nuevo usuario
        </Button>
      </CardHeader>
      <CardContent className="divide-y divide-ink-100 dark:divide-ink-800">
        {users.isLoading ? (
          Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-14 mb-2" />)
        ) : !users.data || users.data.length === 0 ? (
          <p className="text-sm text-ink-500 py-6 text-center">Sin usuarios.</p>
        ) : (
          users.data.map((u) => (
            <div key={u.id} className="py-3 flex items-center gap-3">
              <UserIcon size={18} className="text-ink-400" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-ink-900 dark:text-white">{u.name}</span>
                  <Badge variant="muted">{u.role}</Badge>
                  {u.status !== "active" && <Badge variant="bad">{u.status}</Badge>}
                </div>
                <p className="text-xs text-ink-500">
                  {u.email} · último login{" "}
                  {u.last_login_at ? new Date(u.last_login_at).toLocaleString("es-PE") : "nunca"}
                </p>
              </div>
              <button
                onClick={() => {
                  if (confirm(`¿Eliminar ${u.email}?`)) deleteUser.mutate(u.id);
                }}
                className="text-ink-400 hover:text-bad p-2"
                title="Eliminar"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))
        )}
      </CardContent>
      {showModal && <NewUserModal accountId={id} onClose={() => setShowModal(false)} />}
    </Card>
  );
}

function NewUserModal({ accountId, onClose }: { accountId: number; onClose: () => void }) {
  const create = useCreateUser(accountId);
  const form = useForm<UserInput>({
    resolver: zodResolver(userSchema),
    defaultValues: { role: "viewer" },
  });

  async function onSubmit(data: UserInput) {
    try {
      await create.mutateAsync(data);
      onClose();
    } catch {}
  }

  return (
    <div className="fixed inset-0 bg-ink-950/60 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Nuevo usuario</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
            <div>
              <Label>Nombre</Label>
              <Input className="mt-1" {...form.register("name")} />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" className="mt-1" {...form.register("email")} />
              {form.formState.errors.email && <p className="text-xs text-bad mt-1">{form.formState.errors.email.message}</p>}
            </div>
            <div>
              <Label>Contraseña</Label>
              <Input type="password" className="mt-1" {...form.register("password")} />
              {form.formState.errors.password && <p className="text-xs text-bad mt-1">{form.formState.errors.password.message}</p>}
            </div>
            <div>
              <Label>Rol</Label>
              <select className="mt-1 h-10 w-full rounded-lg border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 px-3 text-sm" {...form.register("role")}>
                <option value="owner">owner</option>
                <option value="admin">admin</option>
                <option value="editor">editor</option>
                <option value="analyst">analyst</option>
                <option value="responder">responder</option>
                <option value="viewer">viewer</option>
                <option value="superadmin">superadmin</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? "Creando…" : "Crear"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ============ Tab API Keys ============

function ApiKeysTab({ id }: { id: number }) {
  const keys = useAdminApiKeys(id);
  const revoke = useRevokeApiKey(id);
  const del = useDeleteApiKey(id);
  const [showModal, setShowModal] = useState(false);
  const [created, setCreated] = useState<AdminApiKeyCreatedOut | null>(null);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>API Keys</CardTitle>
          <CardDescription>Tokens de acceso a la API pública</CardDescription>
        </div>
        <Button size="sm" onClick={() => setShowModal(true)}>
          <Plus size={14} /> Generar nueva
        </Button>
      </CardHeader>
      <CardContent className="divide-y divide-ink-100 dark:divide-ink-800">
        {keys.isLoading ? (
          Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-14 mb-2" />)
        ) : !keys.data || keys.data.length === 0 ? (
          <p className="text-sm text-ink-500 py-6 text-center">Sin API keys.</p>
        ) : (
          keys.data.map((k) => (
            <div key={k.id} className="py-3 flex items-center gap-3">
              <KeyRound size={18} className="text-ink-400" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-ink-900 dark:text-white">{k.name}</span>
                  {k.revoked_at && <Badge variant="bad">revocada</Badge>}
                </div>
                <p className="text-xs font-mono text-ink-500">
                  {k.prefix}••• · creada {new Date(k.created_at).toLocaleString("es-PE")}
                  {k.last_used_at && ` · último uso ${new Date(k.last_used_at).toLocaleString("es-PE")}`}
                </p>
              </div>
              {!k.revoked_at && (
                <button onClick={() => revoke.mutate(k.id)} className="text-warn hover:text-warn/80 text-xs font-medium px-2">
                  Revocar
                </button>
              )}
              <button
                onClick={() => {
                  if (confirm(`¿Eliminar permanentemente ${k.name}?`)) del.mutate(k.id);
                }}
                className="text-ink-400 hover:text-bad p-2"
                title="Eliminar"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))
        )}
      </CardContent>

      {showModal && (
        <NewApiKeyModal
          accountId={id}
          onClose={() => setShowModal(false)}
          onCreated={(k) => {
            setShowModal(false);
            setCreated(k);
          }}
        />
      )}
      {created && <CreatedKeyModal apikey={created} onClose={() => setCreated(null)} />}
    </Card>
  );
}

function NewApiKeyModal({
  accountId,
  onClose,
  onCreated,
}: {
  accountId: number;
  onClose: () => void;
  onCreated: (k: AdminApiKeyCreatedOut) => void;
}) {
  const create = useCreateApiKey(accountId);
  const [name, setName] = useState("");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      const result = await create.mutateAsync({ name });
      onCreated(result);
    } catch {}
  }

  return (
    <div className="fixed inset-0 bg-ink-950/60 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Generar API key</CardTitle>
          <CardDescription>El token se mostrará UNA sola vez. Cópialo antes de cerrar.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Nombre descriptivo</Label>
              <Input className="mt-1" value={name} onChange={(e) => setName(e.target.value)} placeholder="ej. CI pipeline" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
              <Button type="submit" disabled={create.isPending || !name.trim()}>
                {create.isPending ? "Generando…" : "Generar"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function CreatedKeyModal({ apikey, onClose }: { apikey: AdminApiKeyCreatedOut; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-ink-950/60 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle>API key generada</CardTitle>
          <CardDescription className="text-warn">
            ⚠️ Este es el ÚNICO momento en que verás el token. Cópialo ahora.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="font-mono text-sm bg-ink-100 dark:bg-ink-800 p-3 rounded-md break-all">
            {apikey.plaintext}
          </div>
          <Button
            variant="outline"
            onClick={() => {
              navigator.clipboard.writeText(apikey.plaintext);
              toast.success("Copiado al portapapeles");
            }}
            className="w-full"
          >
            <Copy size={14} /> Copiar
          </Button>
          <Button onClick={onClose} className="w-full">Listo</Button>
        </CardContent>
      </Card>
    </div>
  );
}
